/**
 * Item actions ported from reware-admin's Inventory Items portal
 * (inventory-items-api.js / app-api.js). Each action hits the same
 * admin_graphql mutation the admin portal uses.
 */
import { execAdmin } from '@src/apis/reware/utils/exec';
import type {
  AcceptInventoryItemMutation,
  AcceptInventoryItemMutationVariables,
  PrintItemHangtagLabelMutation,
  PrintItemHangtagLabelMutationVariables,
  PrintItemLabelMutation,
  PrintItemLabelMutationVariables,
  RejectInventoryItemMutation,
  RejectInventoryItemMutationVariables,
  ReshootLikeNewPhotographyMutation,
  ReshootLikeNewPhotographyMutationVariables,
  ReviewInventoryItemMutation,
  ReviewInventoryItemMutationVariables,
  SyncShopifyCatalogProductMutation,
  SyncShopifyCatalogProductMutationVariables,
  UpdateInventoryItemMutation,
  UpdateInventoryItemMutationVariables,
  UseCatalogPhotographyMutation,
  UseCatalogPhotographyMutationVariables,
} from '@src/types/__generated/rewareAdminTypes';

// graphql-codegen extracts documents from `gql`-tagged literals; String.raw is
// the package's tag of choice (see src/resalePricing/conditionDiscounts).
const gql = String.raw;

export const acceptItem = async (id: number): Promise<void> => {
  const query = gql`
    mutation acceptInventoryItem($id: Int!) {
      inventoryItem(id: $id) {
        accept {
          id
          state
        }
      }
    }
  `;
  await execAdmin<AcceptInventoryItemMutationVariables, AcceptInventoryItemMutation>(query, { variables: { id } });
};

export const reviewItem = async (id: number): Promise<void> => {
  const query = gql`
    mutation reviewInventoryItem($id: Int!) {
      inventoryItem(id: $id) {
        review {
          id
          state
        }
      }
    }
  `;
  await execAdmin<ReviewInventoryItemMutationVariables, ReviewInventoryItemMutation>(query, { variables: { id } });
};

export const rejectItem = async (
  id: number,
  reason: string,
  note?: string
): Promise<void> => {
  const query = gql`
    mutation rejectInventoryItem($id: Int!, $reason: String!, $note: String) {
      inventoryItem(id: $id) {
        reject(reason: $reason, note: $note) {
          id
          state
          rejectedReason
        }
      }
    }
  `;
  await execAdmin<RejectInventoryItemMutationVariables, RejectInventoryItemMutation>(query, {
    variables: { id, reason, note },
  });
};

export interface UpdateItemInput {
  id: number;
  /** Price in dollars; converted to cents like the admin portal does */
  price: number;
  condition: string;
  notes: string;
}

export const updateItem = async ({ id, price, condition, notes }: UpdateItemInput): Promise<void> => {
  const cents = Math.round(price * 100);
  const query = gql`
    mutation updateInventoryItem(
      $id: Int!
      $price: Int!
      $condition: String!
      $notes: String!
    ) {
      inventoryItem(id: $id) {
        update(price: $price, condition: $condition, notes: $notes) {
          id
          price
          condition
          notes(omitMeasurements: ALWAYS)
        }
      }
    }
  `;
  await execAdmin<UpdateInventoryItemMutationVariables, UpdateInventoryItemMutation>(
    query,
    { variables: { id, price: cents, condition, notes } }
  );
};

export const printItemLabel = async (itemId: number): Promise<void> => {
  const query = gql`
    mutation printItemLabel($itemId: Int!) {
      inventoryItem(id: $itemId) {
        printLabel {
          result
        }
      }
    }
  `;
  await execAdmin<PrintItemLabelMutationVariables, PrintItemLabelMutation>(query, { variables: { itemId } });
};

export const printItemHangtagLabel = async (itemId: number): Promise<void> => {
  const query = gql`
    mutation printItemHangtagLabel($itemId: Int!) {
      inventoryItem(id: $itemId) {
        printHangtagLabel {
          result
        }
      }
    }
  `;
  await execAdmin<PrintItemHangtagLabelMutationVariables, PrintItemHangtagLabelMutation>(query, { variables: { itemId } });
};

export const applyCatalogPhotography = async (itemId: number): Promise<void> => {
  const query = gql`
    mutation useCatalogPhotography($itemId: Int!) {
      inventoryItem(id: $itemId) {
        useCatalogPhotography {
          id
          state
        }
      }
    }
  `;
  await execAdmin<UseCatalogPhotographyMutationVariables, UseCatalogPhotographyMutation>(query, { variables: { itemId } });
};

export const reshootLikeNewPhotography = async (itemId: number): Promise<void> => {
  const query = gql`
    mutation reshootLikeNewPhotography($itemId: Int!) {
      inventoryItem(id: $itemId) {
        reshootLikeNewPhotography {
          id
        }
      }
    }
  `;
  await execAdmin<ReshootLikeNewPhotographyMutationVariables, ReshootLikeNewPhotographyMutation>(query, { variables: { itemId } });
};

export const syncShopifyCatalogProduct = async (
  partnerCode: string,
  parentSku: string
): Promise<string> => {
  const query = gql`
    mutation SyncShopifyCatalogProduct($partnerCode: String!, $parentSku: String!) {
      syncShopifyCatalogProduct(partnerCode: $partnerCode, parentSku: $parentSku) {
        message
      }
    }
  `;
  const data = await execAdmin<
    SyncShopifyCatalogProductMutationVariables,
    SyncShopifyCatalogProductMutation
  >(query, { variables: { partnerCode, parentSku } });
  // The schema types `message` as the opaque JSON scalar (generated as `any`),
  // so coerce rather than trusting it to be a string — it ends up in a toast.
  const message: unknown = data.syncShopifyCatalogProduct?.message;
  return typeof message === 'string' ? message : '';
};
